import {persist} from '@/lib/vatio-persistence';
import {
  AuthentictaionState,
  isCiamEnabled,
  type GoogleUserProfile,
} from '@gfiber/pixel-party';
import {usePathname, useSearchParams} from 'next/navigation';
import {useEffect} from 'react';
import {useCallback} from 'react';
import {proxy, useSnapshot} from 'valtio';
import {StepsPath} from '../routing/StepsRouting';
import {useGoToWithPreserveParams} from '../routing/StepsWizardContext';
import {useAuth0} from '@auth0/auth0-react';
import {useAuth0Login} from '../auth/useWebAuth';

export enum ProfileLoadingStatus {
  LoggedOut = 'LoggedOut',
  Pending = 'Pending',
  LoggedIn = 'LoggedIn',
}

export interface UserProfileState {
  /** Promise allow to use native suspense mechanism */
  user?: GoogleUserProfile | Promise<GoogleUserProfile | undefined>;
  state: AuthentictaionState;
  signOut?: (switchAccount?: boolean) => void;
}

export const store = proxy<UserProfileState>({
  user: undefined,
  state: AuthentictaionState.Unknown,
});

export const useUserProfileState = () => useSnapshot(store);

/*
 * Add this new function and keep old setUserProfile
 * in case any design change.
 */
export const setUserProfileBasedOnGaiaInfo = (
  user: GoogleUserProfile | undefined,
) => {
  /* We only update the profile when the returned email
   * is not the same as the stored one.
   * For the edge case which a user signed in already,
   * then BFF returns a different email,
   * it means the old user signed out and a new user signed in,
   * we are not handle it for now.
   *
   * There will be only logged-in, or not logged-in at this moment.
   */
  // TODO: handle the edge case the old user signed out and a new user signed in.

  if (user?.email) {
    const currentUser = store.user as GoogleUserProfile;
    if (currentUser?.email !== user?.email) {
      store.user = user;
    }
    store.state = AuthentictaionState.Authenticated;
  } else {
    store.user = undefined;
    store.state = AuthentictaionState.Unknown;
  }
  setUserAuthenticationState(!!user?.email);
};

export const markUserAsAuthenticated = () => {
  store.state = AuthentictaionState.Authenticated;
  setUserAuthenticationState(true);
};

export const setUserProfile = (
  user: GoogleUserProfile | undefined,
  state: AuthentictaionState,
) => {
  store.user = user;
  setUserAuthenticationState(state === AuthentictaionState.Authenticated);
  store.state = state;
};

export const logout = (select?: boolean) => {
  store.signOut?.(select);
};

const useLogout = (currentUrl: string) => {
  return useCallback(
    (select?: boolean) => {
      setUserProfile(undefined, AuthentictaionState.Unknown);
      window.location.href = select
        ? getSwitchAccountUrl(currentUrl)
        : getLogoutOnlyUrl();
    },
    [currentUrl],
  );
};

export const useCreateAccountUrl = () => {
  const currentUrl = useEncodeUrlWithLoginParam();
  return getCreateAccountUrl(currentUrl);
};

/**
 * @returns login method for GAIA or CIAM depending on availability
 */
export const useLogin = () => {
  const currentUrl = useEncodeUrlWithLoginParam();

  const gaiaLogin = useCallback(() => {
    window.location.href = getLoginUrl(currentUrl);
  }, [currentUrl]);
  const gaiaLogout = useLogout(currentUrl);

  const ciamAuth = useAuth0Login();
  if (isCiamEnabled()) {
    store.signOut = ciamAuth.logout;
    return {
      ...ciamAuth,
      login: ciamAuth.login,
    };
  }

  store.signOut = gaiaLogout;
  return {
    ...ciamAuth, // fallback default params
    login: gaiaLogin,
  };
};

const getCurrentUrlEncoded = () => encodeURIComponent(window.location.href);
const getLogoutOnlyUrl = () => getLogoutUrl(getCurrentUrlEncoded());
const getSwitchAccountUrl = (currentUrl: string) =>
  getLogoutUrl(encodeURIComponent(getLoginUrl(currentUrl)));

const getCreateAccountUrl = (continueUri: string) => {
  return `https://accounts.google.com/SignUp?service=gfiber&continue=${continueUri}`;
};

const getLogoutUrl = (continueUri: string) => {
  return `https://accounts.google.com/logout?service=gfiber&continue=${continueUri}`;
};

const getLoginUrl = (currentUrl: string) => {
  return `https://accounts.google.com/ServiceLogin?service=gfiber&passive=true&continue=${currentUrl}&followup=${currentUrl}`;
};

export const useAuthorizedUser = () => {
  const {user, state} = useUserProfileState();

  const redirectToStart = useGoToWithPreserveParams();

  useEffect(() => {
    if (state === AuthentictaionState.Failed && !user) {
      redirectToStart(StepsPath.SignIn); // will redirect to sign in if authentication failed
    }
  }, [user, state, redirectToStart]);

  return user;
};

// UserProfileState is not persistent. It is only updated after the initial API response
// according to the header values.  UserAuthenticationState is persistent and can be used
// before the page is loaded, however, we should do another check after the page is loaded
// since the persistant state might be stale.
export interface UserAuthenticationState {
  authenticated?: boolean;
}

// if add any new field, add the new field to the below reset function also
export const authStatePersistStore = persist<UserAuthenticationState>(
  'USER_AUTH_STATE',
  {
    authenticated: undefined,
  },
);

export const setUserAuthenticationState = (authenticated: boolean) => {
  authStatePersistStore.authenticated = authenticated;
};

export const useIsUserAuthenticatedThroughGaia = () => {
  const removeLoginParam = useRemoveLoginParam();
  return useCallback(() => {
    if (removeLoginParam()) {
      markUserAsAuthenticated();
      return true;
    }

    return !!authStatePersistStore.authenticated;
  }, [removeLoginParam]);
};

/** Check if user authenticated in GAIA or CIAM */
export const useIsUserAuthenticated = () => {
  const isUserSignedIn = useIsUserAuthenticatedThroughGaia();
  const {user} = useAuth0();

  return useCallback(() => {
    return isCiamEnabled() ? !!user : isUserSignedIn();
  }, [user, isUserSignedIn]);
};

// URL param passed in log in URL so we know it is from login redirect.
// This is needed so we mark the user as authenticated before we receive
// the user profiel data from the backend. This is nneded so that we
// can remain on a step that requires authentication.
const LOGGED_IN_KEY = 'loggedin';

export const useRemoveLoginParam = () => {
  const searchParams = useSearchParams();
  const pathName = usePathname();
  return useCallback(() => {
    const loggedInParam = searchParams?.get(LOGGED_IN_KEY);
    if (loggedInParam) {
      const current = new URLSearchParams(searchParams.toString());
      current.delete(LOGGED_IN_KEY);
      window.history.replaceState(
        null,
        '',
        `${pathName}?${current.toString()}`,
      );
      return true;
    }

    return false;
  }, [searchParams, pathName]);
};

export const useEncodeUrlWithLoginParam = () => {
  const searchParams = useSearchParams();
  const pathName = usePathname();
  const current = new URLSearchParams(searchParams.toString());
  current.set(LOGGED_IN_KEY, '1');
  return encodeURIComponent(
    `${location.origin}${pathName}?${current.toString()}`,
  );
};
